import { useEffect, useRef, useState } from "react";
import { CreditCard, Download, Loader2, RefreshCw, ShieldCheck, Undo2 } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import { useVisibleInterval } from "@/lib/useVisibleInterval";
import { fetchMobileInvoice, signMobileInvoice, checkoutMobileInvoice, resetMobileInvoice,
  retryMobileReceipt, downloadMobileReceipt, formatApiError } from "@/lib/api";

const button = "flex w-full items-center justify-center gap-2 border border-steel-border px-4 py-3 text-sm transition-colors hover:border-steel-brass disabled:opacity-40";
const money = (value) => Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });

export const MobileInvoicePayment = ({ invoice, adminKey, onChange }) => {
  const [name, setName] = useState(invoice.signer_name || invoice.recipient_name);
  const [signature, setSignature] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const busyRef = useRef(false);
  const refreshRef = useRef(null);
  const paid = invoice.status === "paid";
  const signed = Boolean(invoice.signed_at);
  const cancelled = new URLSearchParams(window.location.search).get("payment") === "cancelled";
  const run = async (fn) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError("");
    try { await fn(); } catch (e) { setError(formatApiError(e)); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const refresh = async () => onChange(await fetchMobileInvoice(adminKey, invoice.id));
  refreshRef.current = () => { if (signed) run(refresh); };
  useEffect(() => { refreshRef.current?.(); }, []);
  useVisibleInterval(() => {
    if ((signed && !paid) || (paid && invoice.receipt_status !== "sent")) run(refresh);
  }, 10000);

  const authorize = () => run(async () => {
    onChange(await signMobileInvoice(adminKey, invoice.id, { signer_name: name.trim(), signature_data: signature, consent }));
    setSignature(null);
  });
  const checkout = () => run(async () => {
    const result = await checkoutMobileInvoice(adminKey, invoice.id);
    if (result.paid) return refresh();
    if (!result.checkout_url) throw new Error("Missing checkout URL");
    window.location.assign(result.checkout_url);
  });
  const reset = () => run(async () => {
    onChange(await resetMobileInvoice(adminKey, invoice.id));
    setSignature(null); setConsent(false);
    toast.success("Authorization cancelled. You can edit and collect a new signature.");
  });

  return <section className="mb-6 min-w-0 space-y-5 border-t-2 border-steel-brass py-6" data-testid="mobile-payment-panel">
    <div className="flex items-center gap-2 text-steel-brass"><ShieldCheck className="h-5 w-5" /><h3 className="font-display text-lg">Mobile payment</h3></div>
    <div className="flex flex-wrap items-baseline justify-between gap-2" data-testid="mobile-payment-total">
      <span className="text-sm text-steel-muted">{paid ? "Total paid" : "Amount to authorize"}</span>
      <strong className="font-display text-2xl">{money(invoice.total)}</strong>
    </div>
    {error && <p role="alert" data-testid="mobile-payment-error" className="break-words text-sm text-red-400">{error}</p>}
    {paid ? <>
      <p data-testid="mobile-payment-approved" className="text-sm font-medium text-emerald-400">Payment approved · Balance $0.00</p>
      <div data-testid="mobile-receipt-status" className="break-words text-sm text-steel-secondary [overflow-wrap:anywhere]">
        {invoice.receipt_status === "sent" ? `Paid invoice and signature emailed to ${invoice.recipient_email}.`
          : invoice.receipt_status === "failed" ? invoice.receipt_error
          : `Paid invoice and signature queued for ${invoice.recipient_email}.`}
      </div>
      {invoice.receipt_status === "failed" && <button data-testid="mobile-receipt-retry" className={button} disabled={busy}
        onClick={() => run(async () => onChange(await retryMobileReceipt(adminKey, invoice.id)))}><RefreshCw className="h-4 w-4" />Retry receipt email</button>}
      <button data-testid="mobile-receipt-download" className={button} disabled={busy}
        onClick={() => run(() => downloadMobileReceipt(adminKey, invoice))}><Download className="h-4 w-4" />Download signed receipt</button>
    </> : !signed ? <>
      <label className="block text-sm text-steel-secondary" htmlFor="mobile-signer-name">Customer’s full name</label>
      <input id="mobile-signer-name" data-testid="mobile-signer-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
        className="w-full min-w-0 border border-steel-border bg-steel-elevated p-3 text-base outline-none focus:border-steel-brass" />
      <SignaturePad onChange={setSignature} />
      <label className="flex items-start gap-3 text-sm leading-relaxed text-steel-secondary">
        <input type="checkbox" data-testid="mobile-payment-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-purple-500" />
        <span data-testid="mobile-authorization-text">I authorize Steele Industries to charge {money(invoice.total)} for invoice {invoice.number}.</span>
      </label>
      <button data-testid="mobile-save-signature" disabled={busy || !signature || !consent || name.trim().length < 2} onClick={authorize}
        className={`${button} bg-steel-accent font-semibold text-white`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}Save authorization
      </button>
    </> : <>
      <p data-testid="mobile-signature-saved" className="text-sm text-emerald-400">Signature saved · {invoice.signer_name}</p>
      {cancelled && <p data-testid="mobile-checkout-cancelled" className="text-sm text-steel-muted">Checkout was closed. This invoice is still unpaid unless Stripe confirms otherwise.</p>}
      <p data-testid="mobile-payment-state" className="text-sm text-steel-secondary">
        {invoice.payment_state === "processing" ? "Stripe is processing this payment. Do not charge again."
          : invoice.payment_state === "expired" ? "Checkout expired. Cancel authorization to start a new attempt."
          : "Ready for card entry on this phone."}
      </p>
      {!["processing", "expired"].includes(invoice.payment_state) && <button data-testid="mobile-open-stripe" onClick={checkout} disabled={busy}
        className={`${button} bg-steel-accent font-semibold text-white`}><CreditCard className="h-4 w-4" />{invoice.payment_state === "checkout" ? "Resume Stripe checkout" : "Enter card in Stripe"}</button>}
      <button data-testid="mobile-refresh-payment" onClick={() => run(refresh)} disabled={busy} className={button}><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />Check payment status</button>
      {invoice.payment_state !== "processing" && <button data-testid="mobile-reset-authorization" onClick={reset} disabled={busy} className={`${button} text-steel-muted`}><Undo2 className="h-4 w-4" />Cancel authorization &amp; edit</button>}
    </>}
  </section>;
};